// EmailSenderDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, Select, MenuItem,
  Chip, ListItemText, Checkbox, InputAdornment
} from '@mui/material';
import { FaPaperclip, FaEnvelope, FaUsers, FaUser, FaUserPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import baseURL from '../../../user/config';

const MS_COLORS = {
  primary: '#2D4A3D',
  text: '#323130',
  textSecondary: '#605D5C',
  background: '#FFFFFF',
  border: '#E1E1E1',
  white: '#FFFFFF',
};

const EmailSenderDialog = ({ open, handleClose, campaigns, allDonors, donorsForCampaign, selectedCampaignId, fetchAllDonors }) => {
  const [emailForm, setEmailForm] = useState({
    subject: '',
    recipientType: 'campaign', // 'campaign', 'all', or 'custom'
    recipients: [],
    customEmails: '',
    body: '',
    attachment: null,
  });
  const [openAddDonor, setOpenAddDonor] = useState(false);
  const [newDonor, setNewDonor] = useState({ firstName: '', lastName: '', email: '' });

  useEffect(() => {
    setEmailForm({
      subject: '',
      recipientType: 'campaign',
      recipients: [],
      customEmails: '',
      body: '',
      attachment: null,
    });
  }, [open]);

  useEffect(() => {
    if (donorsForCampaign.length > 0) {
      setEmailForm(prev => ({
        ...prev,
        recipients: donorsForCampaign.map(d => d.id)
      }));
    } else {
      setEmailForm(prev => ({ ...prev, recipients: [] }));
    }
  }, [donorsForCampaign]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEmailForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAttachmentChange = (e) => {
    if (e.target.files.length > 0) {
      setEmailForm(prev => ({ ...prev, attachment: e.target.files[0] }));
    }
  };

  const handleRecipientTypeChange = (e) => {
    const { value } = e.target;
    setEmailForm(prev => ({
      ...prev,
      recipientType: value,
      recipients: value === 'all' ? allDonors.map(d => d.id) : [],
    }));
  };

  const handleRecipientsChange = (e) => {
    const { value } = e.target;
    const currentDonors = emailForm.recipientType === 'all' ? allDonors : donorsForCampaign;
    if (value.includes('all')) {
      setEmailForm(prev => ({
        ...prev,
        recipients: prev.recipients.length < currentDonors.length ? currentDonors.map(d => d.id) : []
      }));
    } else {
      setEmailForm(prev => ({ ...prev, recipients: value }));
    }
  };

  const handleAddDonorInputChange = (e) => {
    const { name, value } = e.target;
    setNewDonor(prev => ({ ...prev, [name]: value }));
  };

  const handleAddDonorSubmit = async () => {
    if (!newDonor.firstName || !newDonor.email) {
      toast.error("Please fill in first name and email.");
      return;
    }

    try {
      const res = await fetch(`${baseURL}/org/add-donor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newDonor),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to add donor.");
      toast.success("Donor added successfully!");
      setNewDonor({ firstName: '', lastName: '', email: '' });
      setOpenAddDonor(false);
      fetchAllDonors(); // Fetch updated donor list from parent
    } catch (err) {
      console.error("Add donor error:", err);
      toast.error(err.message || "Failed to add donor.");
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.body) {
      toast.error("Please fill in the subject and email body.");
      return;
    }
    
    if (emailForm.recipientType === 'custom' && !emailForm.customEmails) {
      toast.error("Please enter at least one custom email address.");
      return;
    }
    
    if ((emailForm.recipientType === 'campaign' || emailForm.recipientType === 'all') && emailForm.recipients.length === 0) {
      toast.error("Please select at least one recipient.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("subject", emailForm.subject);
      formData.append("message", emailForm.body);
      formData.append("recipientType", emailForm.recipientType);
      
      if (emailForm.recipientType === 'custom') {
        formData.append("customEmails", emailForm.customEmails);
      } else {
        formData.append("campaignId", selectedCampaignId);
        emailForm.recipients.forEach(id => formData.append("recipients", id));
      }
      
      if (emailForm.attachment) {
        formData.append("attachment", emailForm.attachment);
      }

      const res = await fetch(`${baseURL}/org/email/send-email`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to send email.");
      toast.success(data.message || "Email sent successfully!");
      handleClose();
    } catch (err) {
      console.error("Send email error:", err);
      toast.error(err.message || "Failed to send email.");
    }
  };

  const renderSelectedChips = (selected, donorList) => {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map(id => {
          const donor = donorList.find(d => d.id === id);
          return <Chip key={id} label={`${donor?.firstName} ${donor?.lastName}` || 'Unknown'} />;
        })}
      </Box>
    );
  };

  const donorList = emailForm.recipientType === 'all' ? allDonors : donorsForCampaign;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>
        Compose New Email
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <FormControl fullWidth>
            <label>Select Recipient Group</label>
            <Select
              name="recipientType"
              value={emailForm.recipientType}
              onChange={handleRecipientTypeChange}
              startAdornment={<InputAdornment position="start"><FaUser /></InputAdornment>}
            >
              <MenuItem value="campaign" disabled={!selectedCampaignId}>Donors from selected Campaign</MenuItem>
              <MenuItem value="all">All Donors</MenuItem>
              <MenuItem value="custom">Custom Email Addresses</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={() => setOpenAddDonor(true)}
            startIcon={<FaUserPlus />}
            sx={{ flexShrink: 0, textTransform: 'none' }}
          >
            Add New Donor
          </Button>
        </Box>

        {emailForm.recipientType === 'custom' ? (
          <TextField
            margin="dense"
            name="customEmails"
            label="Recipient Email Addresses (comma separated)"
            type="text"
            fullWidth
            variant="outlined"
            value={emailForm.customEmails}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><FaEnvelope /></InputAdornment>), }}
          />
        ) : (
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <label>Select Recipients</label>
            <Select
              multiple
              value={emailForm.recipients}
              onChange={handleRecipientsChange}
              renderValue={(selected) => renderSelectedChips(selected, donorList)}
              startAdornment={<InputAdornment position="start"><FaUsers /></InputAdornment>}
            >
              <MenuItem value="all">
                <Checkbox checked={emailForm.recipients.length === donorList.length && donorList.length > 0} />
                <ListItemText primary="Select All" />
              </MenuItem>
              {donorList.map(d => (
                <MenuItem key={d.id} value={d.id}>
                  <Checkbox checked={emailForm.recipients.includes(d.id)} />
                  <ListItemText primary={`${d.firstName} ${d.lastName} (${d.email})`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <label>Subject</label>
        <TextField
          margin="dense"
          name="subject"
          label="Subject"
          type="text"
          fullWidth
          variant="outlined"
          value={emailForm.subject}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><FaEnvelope /></InputAdornment>), }}
        />

        <label>Email Body</label>
        <TextField
          margin="dense"
          name="body"
          label="Email Body"
          multiline
          rows={10}
          fullWidth
          variant="outlined"
          value={emailForm.body}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
        />

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<FaPaperclip />}
            sx={{ textTransform: 'none', color: MS_COLORS.textSecondary }}
          >
            {emailForm.attachment ? 'Change Attachment' : 'Add Attachment'}
            <input type="file" hidden onChange={handleAttachmentChange} />
          </Button>
          {emailForm.attachment && (
            <Typography variant="body2" sx={{ ml: 2, color: MS_COLORS.textSecondary }}>
              {emailForm.attachment.name}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} sx={{ color: MS_COLORS.text, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSendEmail}
          variant="contained"
          sx={{
            backgroundColor: MS_COLORS.primary,
            color: MS_COLORS.white,
            textTransform: 'none',
            boxShadow: 0,
            '&:hover': { backgroundColor: MS_COLORS.primary, opacity: 0.9, boxShadow: 0 },
          }}
        >
          Send Email
        </Button>
      </DialogActions>

      {/* Add New Donor Dialog */}
      <Dialog open={openAddDonor} onClose={() => setOpenAddDonor(false)}>
        <DialogTitle>Add New Donor</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="firstName"
            label="First Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newDonor.firstName}
            onChange={handleAddDonorInputChange}
          />
          <TextField
            margin="dense"
            name="lastName"
            label="Last Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newDonor.lastName}
            onChange={handleAddDonorInputChange}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newDonor.email}
            onChange={handleAddDonorInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDonor(false)}>Cancel</Button>
          <Button onClick={handleAddDonorSubmit} variant="contained">Add Donor</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EmailSenderDialog;